import { useState, useEffect, useCallback, memo } from "react";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import { obtenerTutoriales } from "../../functions/consultas";
import { crearRegistro, actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import BotonSOS from "./sos/boton_sos";
import "bootstrap-icons/font/bootstrap-icons.css";

const columnas = [
  { label: "Título", key: "titulo" },
  { label: "Administrador", key: "administradorTexto" },
  { label: "Residente", key: "residenteTexto" },
  { label: "Guarda", key: "guardaTexto" }
];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const RUTA_TUTORIALES = "tutoriales";

const puedeGestionar = () => localStorage.getItem("Permisos") === "SUPER";

const escaparHtml = (texto = "") =>
  String(texto).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

const extraerIdYoutube = (valor) => {
  const texto = (valor || "").trim();
  if (!texto) return "";

  const patrones = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([A-Za-z0-9_-]{11})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];

  for (const patron of patrones) {
    const encontrado = texto.match(patron);
    if (encontrado) return encontrado[1];
  }

  return /^[A-Za-z0-9_-]{11}$/.test(texto) ? texto : "";
};

const generarFormularioHTML = (tutorial = {}) => `
  <style>
    #tut-form .campo-label { display:block; margin:12px 0 6px; color:#555; font-size:14px; font-weight:500; }
    #tut-form .campo-input { width:100%!important; box-sizing:border-box!important; padding:10px!important; border:1px solid #d1d5db!important; border-radius:5px!important; font-size:14px!important; background:white!important; }
    #tut-form .campo-hint { font-size:12px; color:#7f8c8d; margin-top:5px; }
    #tut-form .grupo-roles { display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:10px; margin-top:10px; }
    #tut-form .item-rol { display:flex; align-items:center; gap:8px; padding:10px; background:white; border:1px solid #e3e8ef; border-radius:5px; cursor:pointer; }
    #tut-form .item-rol input { width:16px; height:16px; cursor:pointer; }
    #tut-form .item-rol label { cursor:pointer; font-size:13px; color:#2c3e50; font-weight:500; margin:0; }
    #tut-form .seccion { background:#f8f9fa; border-radius:8px; padding:16px; margin-top:14px; border:1px solid #e3e8ef; }
    #tut-form .seccion-titulo { color:#2c3e50; font-size:14px; font-weight:700; margin-bottom:6px; }
  </style>
  <div id="tut-form" style="text-align:left;padding:6px 10px;">
    <label class="campo-label" for="tutTitulo">Título</label>
    <input id="tutTitulo" class="campo-input" value="${escaparHtml(tutorial.titulo)}" placeholder="Ej. Cómo crear un pase de visitante" maxlength="120">

    <label class="campo-label" for="tutUrl">Video de YouTube</label>
    <input id="tutUrl" class="campo-input" value="${escaparHtml(tutorial.url)}" placeholder="https://www.youtube.com/watch?v=XXXXXXXXXXX">
    <p class="campo-hint">Pegue el enlace completo o solo el ID del video. Se guarda únicamente el ID, igual que la app móvil.</p>

    <div class="seccion">
      <div class="seccion-titulo">¿Quiénes pueden ver este tutorial?</div>
      <div class="grupo-roles">
        <div class="item-rol">
          <input id="tutAdministrador" type="checkbox" ${tutorial.administrador ? "checked" : ""}>
          <label for="tutAdministrador">Administrador</label>
        </div>
        <div class="item-rol">
          <input id="tutResidente" type="checkbox" ${tutorial.residente ? "checked" : ""}>
          <label for="tutResidente">Residente</label>
        </div>
        <div class="item-rol">
          <input id="tutGuarda" type="checkbox" ${tutorial.guarda ? "checked" : ""}>
          <label for="tutGuarda">Guarda</label>
        </div>
      </div>
    </div>
  </div>
`;

const leerFormulario = () => {
  const titulo = document.getElementById("tutTitulo")?.value.trim() || "";
  const urlCruda = document.getElementById("tutUrl")?.value.trim() || "";
  const administrador = document.getElementById("tutAdministrador")?.checked || false;
  const residente = document.getElementById("tutResidente")?.checked || false;
  const guarda = document.getElementById("tutGuarda")?.checked || false;

  if (!titulo) {
    Swal.showValidationMessage("Debe agregar un título para el tutorial.");
    return false;
  }

  const url = extraerIdYoutube(urlCruda);
  if (!url) {
    Swal.showValidationMessage("Ingrese un enlace de YouTube válido o el ID de 11 caracteres.");
    return false;
  }

  if (!administrador && !residente && !guarda) {
    Swal.showValidationMessage("Seleccione al menos un rol que pueda ver el tutorial.");
    return false;
  }

  return { titulo, url, administrador, residente, guarda };
};

const ListadoTutoriales = memo(() => {
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const gestionar = puedeGestionar();

  const capturarDatos = useCallback(async () => {
    try {
      const data = await obtenerTutoriales();
      if (data) {
        const datosTransformados = Object.entries(data).map(([id, item]) => ({
          ...item,
          id,
          administradorTexto: String(item.administrador) === "true" ? "SI" : "NO",
          residenteTexto: String(item.residente) === "true" ? "SI" : "NO",
          guardaTexto: String(item.guarda) === "true" ? "SI" : "NO"
        }));
        setDatos(datosTransformados);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    capturarDatos();
  }, [capturarDatos]);

  const guardarTutorial = useCallback(async (tutorial = null) => {
    const esEdicion = Boolean(tutorial);

    const { value: valores } = await Swal.fire({
      title: esEdicion ? "Editar tutorial" : "Agregar tutorial",
      html: generarFormularioHTML(
        esEdicion
          ? {
              ...tutorial,
              administrador: String(tutorial.administrador) === "true",
              residente: String(tutorial.residente) === "true",
              guarda: String(tutorial.guarda) === "true",
            }
          : {}
      ),
      width: "600px",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
      focusConfirm: false,
      preConfirm: leerFormulario,
    });

    if (!valores) return;

    try {
      if (esEdicion) {
        await actualizarRegistro(`${RUTA_TUTORIALES}/${tutorial.id}`, valores);
      } else {
        await crearRegistro(RUTA_TUTORIALES, valores);
      }

      await Swal.fire({
        icon: "success",
        title: esEdicion ? "Tutorial actualizado" : "Tutorial agregado",
        timer: 2000,
        showConfirmButton: false,
      });

      capturarDatos();
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo guardar el tutorial." });
    }
  }, [capturarDatos]);

  const eliminarTutorial = useCallback(async (tutorial) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar tutorial?",
      text: `Se eliminará "${tutorial.titulo}" de forma permanente.`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    try {
      await eliminarRegistro(`${RUTA_TUTORIALES}/${tutorial.id}`);
      Swal.fire({ icon: "success", title: "Eliminado", timer: 1800, showConfirmButton: false });
      capturarDatos();
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar el tutorial." });
    }
  }, [capturarDatos]);

  const botonesAccion = [
    { texto: "Ver Video", icono: "bi-play-circle", accion: (fila) => {
        if (fila.url) {
          window.open(`https://www.youtube.com/watch?v=${fila.url}`, "_blank", "noopener,noreferrer");
        } else {
          Swal.fire({ icon: "info", title: "Sin video", text: "Este tutorial no tiene video disponible." });
        }
      }
    },
    ...(gestionar
      ? [
          { texto: "Editar", icono: "bi-pencil", accion: guardarTutorial },
          { texto: "Eliminar", icono: "bi-trash", accion: eliminarTutorial },
        ]
      : []),
  ];

  return cargando ? (
    <AnimacionCarga />
  ) : (
    <>
      <TablaGenerica
        datos={datos}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={500}
        botones={botonesAccion}
        contenidoExtra={
          <>
            <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
            <CSVLink data={datos} headers={columnas} filename="listado_tutoriales.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
            {gestionar && (
              <button onClick={() => guardarTutorial()} title="Agregar Tutorial"><i className="bi bi-plus-lg" /></button>
            )}
          </>
        }
      />
      <BotonSOS />
    </>
  );
});

export default ListadoTutoriales;
